<template lang="pug">
.praca-form
  .cabecalho.row.ab.col-s
    h3 {{ edicao ? 'ALTERAR PRAÇA' : 'NOVA PRAÇA' }}
    span.sub(v-if="cidadeFixa") {{ cidade.uf }} / {{ cidade.nome }}
    span.sub(v-else) escolha a cidade, o tipo de serviço e o CEP de referência
  .row.js.col-s.campos
    .col.g4.g12-s
      label(:for="'cidade-' + chave") CIDADE
      input.input(v-if="cidadeFixa" :id="'cidade-' + chave" :value="cidade.uf + ' / ' + cidade.nome" readonly)
      SeletorFiltro(v-else :id-campo="'cidade-' + chave" v-model="cidadeEscolhida" :busca="buscarCidades" placeholder="Nome da cidade (2+ letras)")
      small(v-if="edicao") A cidade da praça não muda. Para trocar, remova esta praça e cadastre outra.
      small(v-else-if="cidadeFixa") Cidade da pendência, já escolhida.
      small(v-else) Do cadastro de cidades do portal. Cidade que já tem praça é recusada.
    .col.g7.g12-s
      label(:for="'tipo-' + chave") TIPO DE SERVIÇO
      SeletorFiltro(:id-campo="'tipo-' + chave" :itens="tipos" v-model="tipo" :filtro-inicial="nomeCidade" placeholder="Filtre pelo nome ou pelo id")
      small Só os tipos ativos da base de custo do cliente V.tal. Pré-filtrado pela cidade, mas a cidade pode usar o tipo de outra praça.
  .cep(v-if="cidadeAtual")
    label CEP DE REFERÊNCIA
    .row.js.col-s.caminhos
      .caminho.col.g4.g12-s
        h4 CEPs de instalação das atividades
        .carregando.row.ac(v-if="sug.carregando")
          Loader(layout="mini")
          span Conferindo os CEPs das atividades no serviço de CEP…
        p.msg.erro(v-else-if="sug.erro") {{ sug.erro }}
        p.msg.vazio(v-else-if="!sug.lista.length") Ainda não há CEP de instalação conhecido para esta cidade. Busque pelo logradouro ou informe o CEP.
        button.sugestao(type="button" v-for="s in sug.lista" :key="s.cep" :class="{ sel: s.cep === cepDigitos }" @click="usar(s.cep)")
          b {{ s.cep | cep }}
          span {{ s.endereco.logradouro }}, {{ s.endereco.bairro }}
          small em {{ s.sas }} atividade(s) · última agenda {{ s.ultima_agenda | date }}
      .caminho.col.g4.g12-s
        h4 Busca por logradouro
        p.msg.aviso(v-if="busca.indisponivel") Busca por logradouro indisponível no momento. Use as sugestões ou informe o CEP.
        template(v-else)
          .row.ac
            input.input(:id="'busca-' + chave" v-model="busca.texto" placeholder="Trecho do logradouro (mín. 3 letras)" @keydown.enter.prevent="buscar")
            Button(layout="green-s" :disabled="!podeBuscar" :loader="busca.carregando" @click="buscar" style="margin-left: 8px") BUSCAR
          small Dica: "Rua" traz os CEPs do centro.
          p.msg.vazio(v-if="busca.feita && !busca.resultados.length") Nenhum logradouro de {{ nomeCidade }} com esse trecho.
          .resultados
            button.sugestao(type="button" v-for="r in busca.resultados" :key="r.cep" :class="{ sel: r.cep === cepDigitos }" @click="usar(r.cep)")
              b {{ r.cep | cep }}
              span {{ r.logradouro }}, {{ r.bairro }}
      .caminho.col.g4.g12-s
        h4 CEP digitado
        input.input.cep-input(:id="'cep-' + chave" v-model="cep" v-mask="'##.###-###'" placeholder="00.000-000" inputmode="numeric" @blur="conferir" @keydown.enter.prevent="enviar")
        .carregando.row.ac(v-if="conf.estado === 'carregando'")
          Loader(layout="mini")
          span Conferindo se o CEP é da cidade…
        p.msg.ok(v-else-if="conf.estado === 'ok'") ✓ {{ conf.endereco.logradouro }}, {{ conf.endereco.bairro }} · {{ conf.endereco.cidade }}/{{ conf.endereco.uf }}
        p.msg.erro(v-else-if="conf.estado === 'erro'") {{ conf.motivo }}
        small Conferido ao sair do campo, pelo código IBGE do município. O cadastro refaz a prova.
  .acoes.row.ac.col-s
    Button(layout="red-s" @click="$emit('cancelar')") CANCELAR
    Button.enviar(:layout="edicao ? 'green-s' : 'green-go'" :disabled="!podeEnviar" :loader="enviando" @click="enviar" style="margin-left: 12px") {{ edicao ? 'ATUALIZAR' : 'CADASTRAR' }}
    small(v-if="!podeEnviar") Para enviar: cidade, tipo de serviço e CEP com oito dígitos.
    small(v-else-if="conf.estado === 'erro'") O CEP não confere. O envio é permitido, mas o backend recusa com o mesmo motivo.
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import { VueMaskDirective } from "v-mask";
import Button from "@/components/Button";
import Loader from "@/components/Loader";
import SeletorFiltro from "@/views/admin/components/SeletorFiltro";

const somenteDigitos = (v) => String(v || "").replace(/\D/g, "")
const formatarCep = (d) => d.length === 8 ? `${d.slice(0, 2)}.${d.slice(2, 5)}-${d.slice(5)}` : d
const MSG_CEP_FORA = "Serviço de CEP indisponível no momento. Tente novamente em instantes."
const semConferencia = () => ({ estado: "", motivo: "", endereco: null })

// Formulário de praça V.tal: cidade (travada quando vem da pendência ou da praça, ou buscada
// no cadastro de cidades), tipo de serviço elegível e CEP de referência pelos três caminhos do
// roteiro (sugestões das atividades, busca por logradouro, digitado com conferência no blur).
// Com a prop `praca`, altera em vez de cadastrar: PUT com o etag da leitura; 412 vira `conflito`.
export default {
  name: "PracaForm",
  components: { Button, Loader, SeletorFiltro },
  directives: { mask: VueMaskDirective },
  props: {
    cidade: Object,
    praca: Object,
    tipos: { type: Array, default: () => [] },
  },
  data() {
    return {
      cidadeEscolhida: null, tipo: null, cep: "", enviando: false, pedidoSugestoes: 0,
      sug: { carregando: false, lista: [], erro: "" },
      busca: { texto: "", resultados: [], carregando: false, feita: false, indisponivel: false },
      conf: semConferencia(),
    };
  },
  computed: {
    chave() { return this.praca ? "p" + this.praca.id : this.cidade ? "c" + this.cidade.id : "nova" },
    edicao() { return !!this.praca },
    cidadeFixa() { return !!this.cidade },
    cidadeAtual() {
      if (this.cidade) return this.cidade
      const c = this.cidadeEscolhida
      return c ? { id: c.id, uf: c.uf, nome: c.cidade } : null
    },
    nomeCidade() { return this.cidadeAtual ? this.cidadeAtual.nome : "" },
    cepDigitos() { return somenteDigitos(this.cep) },
    podeBuscar() { return this.busca.texto.trim().length >= 3 && !this.busca.carregando },
    podeEnviar() { return !!(this.cidadeAtual && this.tipo && this.cepDigitos.length === 8) },
  },
  watch: {
    cidadeAtual: {
      immediate: true,
      handler(c, antes) { if (c && (!antes || antes.id !== c.id)) this.iniciarCep() },
    },
    praca: {
      immediate: true,
      handler(p) { if (p) this.preencher(p) },
    },
  },
  methods: {
    ...mapActions("admin", ["PRACA_SUGESTOES", "PRACA_BUSCA_CEP", "PRACA_CONFERE_CEP", "PRACA_NOVA", "PRACA_EDITAR"]),
    ...mapActions("general", ["LOAD_CITIES"]),
    ...mapMutations("general", ["SET_STATUS"]),
    async buscarCidades(texto) {
      try {
        const { data } = await this.LOAD_CITIES({ cidade: texto })
        return (data || []).map(c => ({ id: c.id, nome: c.cidade_uf, uf: c.estado, cidade: c.cidade }))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("PracaForm: busca de cidades falhou", e)
        this.SET_STATUS({ text: "Não foi possível buscar cidades. Tente de novo.", color: "orange" })
        return []
      }
    },
    preencher(p) {
      this.tipo = this.tipos.find(t => t.id === p.tipo_servico.id) || { id: p.tipo_servico.id, nome: p.tipo_servico.nome }
      this.cep = formatarCep(somenteDigitos(p.cep_referencia))
      this.conf = semConferencia()
    },
    iniciarCep() {
      this.sug = { carregando: false, lista: [], erro: "" }
      this.busca = { texto: "", resultados: [], carregando: false, feita: false, indisponivel: false }
      this.conf = semConferencia()
      if (!this.praca) this.cep = ""
      this.carregarSugestoes()
    },
    async carregarSugestoes() {
      const pedido = ++this.pedidoSugestoes
      this.sug.carregando = true
      const r = await this.PRACA_SUGESTOES(this.cidadeAtual.id)
      if (pedido !== this.pedidoSugestoes) return
      this.sug.carregando = false
      if (r.status == 200) this.sug.lista = r.data
      else this.sug.erro = r.status == 503 ? MSG_CEP_FORA : "Não foi possível buscar os CEPs das atividades."
    },
    async buscar() {
      if (!this.podeBuscar) return
      this.busca.carregando = true
      const r = await this.PRACA_BUSCA_CEP({ idCidade: this.cidadeAtual.id, logradouro: this.busca.texto.trim() })
      this.busca.carregando = false
      this.busca.feita = true
      if (r.status == 200) this.busca.resultados = r.data
      else if (r.status == 503) this.busca.indisponivel = true
    },
    usar(cep) {
      this.cep = formatarCep(somenteDigitos(cep))
      this.conferir()
    },
    async conferir() {
      if (!this.cepDigitos) { this.conf = semConferencia(); return }
      if (this.cepDigitos.length !== 8) { this.conf = { estado: "erro", motivo: "CEP deve ter oito dígitos.", endereco: null }; return }
      this.conf = { estado: "carregando", motivo: "", endereco: null }
      const r = await this.PRACA_CONFERE_CEP({ idCidade: this.cidadeAtual.id, cep: this.cepDigitos })
      if (r.status != 200) { this.conf = semConferencia(); return }
      this.conf = r.data.coerente
        ? { estado: "ok", motivo: "", endereco: r.data.endereco }
        : { estado: "erro", motivo: r.data.motivo, endereco: r.data.endereco }
    },
    async enviar() {
      if (!this.podeEnviar || this.enviando) return
      this.enviando = true
      const r = this.edicao
        ? await this.PRACA_EDITAR({ id: this.praca.id, id_tipo_servico: this.tipo.id, cep_referencia: this.cepDigitos, etag: this.praca.etag })
        : await this.PRACA_NOVA({ id_cidade: this.cidadeAtual.id, id_tipo_servico: this.tipo.id, cep_referencia: this.cepDigitos })
      this.enviando = false
      if (r.status == 200) this.$emit("salvo", r.data)
      else if (r.status == 412) this.$emit("conflito", this.praca.id)
    },
  },
};
</script>

<style lang="sass" scoped>
@use "sass:color"
@use "@/assets/scss/vars.scss" as *
.praca-form
  background: #f9f9f9
  border: 1.6px solid #ccc
  border-radius: 16px
  padding: 22px
  margin-top: 18px
  label
    @include label
    display: block
  small
    display: block
    color: $gray-5
    font-size: 12px
    line-height: 1.45
    margin-top: 4px
  .input
    width: 100%
    box-sizing: border-box
  .cabecalho
    margin-bottom: 18px
    h3
      margin: 0
      color: $purple
      font-size: 15px
      letter-spacing: .5px
    .sub
      color: $gray-5
      font-size: 13px
      margin-left: 14px
  .campos
    gap: 22px
  .cep
    margin-top: 22px
  .caminhos
    gap: 18px
  .caminho
    background: white
    border: 1px solid $white-6
    border-radius: 12px
    padding: 14px
    min-width: 0
    h4
      margin: 0 0 10px
      font-size: 12px
      color: $gray-5
      text-transform: uppercase
      letter-spacing: .6px
    .row .input
      flex: 1
  .carregando
    color: $gray-5
    font-size: 13px
    .loader
      margin-right: 10px
  .sugestao
    display: flex
    flex-direction: column
    gap: 2px
    width: 100%
    text-align: left
    background: white
    border: 1.6px solid $white-6
    border-radius: 8px
    padding: 8px 12px
    margin-top: 6px
    cursor: pointer
    font: inherit
    color: $font-color
    &:hover
      border-color: $purple
      background: rgba($purple, .08)
    &.sel
      border-color: $green-6
      background: $green-3
    b
      font-size: 15px
    span
      font-size: 13px
    small
      margin: 0
  .resultados
    max-height: 220px
    overflow: auto
  .cep-input
    font-size: 16px
    letter-spacing: 1px
  .msg
    margin: 8px 0 0
    font-size: 13px
    line-height: 1.45
    padding: 8px 10px
    border-radius: 8px
    &.ok
      background: $green-3
      color: $green-6
    &.erro
      background: $red-6
      color: $red
    &.aviso
      background: rgba($orange-1, .15)
      color: color.adjust($orange-1, $lightness: -25%)
    &.vazio
      background: $white-8
      color: $gray-5
  .acoes
    margin-top: 22px
    small
      margin: 0 0 0 14px
</style>
